import React, { FC, useEffect, useState } from 'react';
import {
    GLOBALGTM_ID,
    //PRIVACYPOLICY_URL,
    //CONSENTSTORAGE_KEY,
    //CONSENTEXPIRATION_MS
} from '../constants';
// Fix for TS1208: Ensures the file is treated as a module 
export { };

// --- Global Type Declarations for GTM ---

// Simplified Gtag definition for the initial dataLayer stub
type Gtag = (...args: any[]) => void;

declare global {
    interface Window {
        dataLayer: any[];
        gtag: Gtag;
    }
}

// --- Configuration Constants ---

// 1. The static, global GTM container ID (We define it here to avoid import errors)
const GLOBAL_GTM_ID: string = typeof GLOBALGTM_ID !== 'undefined' ? GLOBALGTM_ID : 'GTM-XXXXXXX';

// 2. The dynamic, client-provided GTM ID 
const CLIENT_GTM_ID: string | null = localStorage.getItem('CLIENTGTMID') ?? null;

// --- GTM NoScript Component (Kept for accessibility/fallback) ---

interface GtmNoScriptProps {
    id: string;
}

const GtmNoScript: FC<GtmNoScriptProps> = ({ id }) => (
    // The noscript iframe should be placed immediately after the opening <body> tag.
    <noscript>
        <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${id}`}
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
            title={`Google Tag Manager ${id}`}
        ></iframe>
    </noscript>
);

// --- New Core Component: GTMConsent ---

/**
 * Initializes the dataLayer stub, injects the Termly script, and injects the GTM script(s).
 * Termly will automatically manage consent and the GTM script execution.
 */
const GTMConsent: FC = () => {
    const [isDismissed, setIsDismissed] = useState(true);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        // Check if scripts have already been injected to prevent duplicates
        if (document.getElementById('termly-script-manager')) {
            return;
        }

        // --- 1. The gtag dataLayer stub (MUST be first) ---
        // This allows Termly to find and hijack the dataLayer/gtag functions.
        if (!window.dataLayer) {
            window.dataLayer = [];
            window.gtag = function (...args: any[]) {
                window.dataLayer.push(arguments);
            };
        }

        // --- 2. Inject Termly Script ---
        // IMPORTANT: Replace 'YOUR_TERMLY_WEBSITE_ID' with your actual Termly ID.
        // This script loads the banner, sets default consent, and monitors for GTM.
        const termlyScript = document.createElement('script');
        termlyScript.id = 'termly-script-manager';
        termlyScript.async = true;
        termlyScript.src = 'https://app.termly.io/resource-blocker/176552b8-ae8f-4d63-ae0c-90290c5ae967?autoBlock=on';
        document.head.appendChild(termlyScript);


        // --- 3. GTM Container Scripts ---

        // Inject GTM Script(s). Termly will automatically monitor and block this script until consent is given.
        const gtmIds: (string | null)[] = [GLOBAL_GTM_ID];
        if (CLIENT_GTM_ID) {
            gtmIds.push(CLIENT_GTM_ID);
        }

        gtmIds.filter((id): id is string => id !== null).forEach((id, index) => {
            const script = document.createElement('script');
            script.id = `gtm-container-script-${index}`;
            script.async = true;
            // Termly relies on the standard GTM URL to manage/block it.
            script.src = `https://www.googletagmanager.com/gtm.js?id=${id}&l=dataLayer`;
            document.head.appendChild(script);
        });

    }, []); // Runs once on app load

    return (
        <div className="fixed inset-x-0 bottom-0 flex justify-center items-end">

            {!isDismissed && (
                <div className="bg-white p-8 rounded-xl shadow-2xl border-t-4 border-indigo-600 text-center max-w-lg relative">

                    {/* Close Button */}
                    <button
                        onClick={() => setIsDismissed(true)}
                        className="absolute top-3 right-3 p-2 text-gray-400 hover:text-gray-600 transition duration-150 rounded-full hover:bg-gray-100 focus:outline-none"
                        aria-label="Close message"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 6 6 18" />
                            <path d="m6 6 12 12" />
                        </svg>
                    </button>

                    {/* Content */}
                    <h1 className="text-3xl font-extrabold text-gray-900 mb-4">Consent Management Delegated</h1>
                    <p className="text-gray-600 mb-6">
                        The **Termly** script is now actively managing consent state, displaying the cookie banner, and controlling the loading of the Google Tag Manager scripts.
                    </p>
                    <p className="text-sm font-medium text-indigo-700">
                        Action required: Please make sure you replace the placeholder <code className="bg-indigo-50 text-indigo-800 p-1 rounded">YOUR_TERMLY_WEBSITE_ID</code> in the generated script with your unique Termly identifier for the system to function correctly.
                    </p>
                </div>
            )}

            {/* GTM NoScript Fallback (Always rendered) */}
            <GtmNoScript id={GLOBAL_GTM_ID} />
            {CLIENT_GTM_ID && <GtmNoScript id={CLIENT_GTM_ID} />}
        </div>
    );
};
export default GTMConsent;
